// Cliente HTTP del panel (docs/01-contratos.md, «API privada»). Mismo origen y cookie de sesión HttpOnly;
// el token CSRF vive solo en memoria y se recupera con GET /auth/session. Nunca se usa localStorage.
import type { ApiError as ApiErrorBody } from '@hilo/contracts';

const API_PREFIX = '/api/v1';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/** Respuesta de error de la API: el servidor sí contestó y el resultado es definitivo. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId: string | null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Sin respuesta (red caída, servidor inaccesible o petición cortada): el resultado es incierto. */
export class NetworkError extends Error {
  constructor() {
    super('No hay conexión con Hilo');
    this.name = 'NetworkError';
  }
}

/**
 * Un proxy delante de Hilo (Vite en desarrollo, el proxy inverso en producción) contestó en su lugar con
 * 502, 503 o 504 y sin el formato de la API: Hilo no está disponible, normalmente durante un reinicio.
 * Es un estado pasajero, distinto de un error de la aplicación.
 */
export const UNREACHABLE = 'SERVICE_UNREACHABLE';
const GATEWAY_STATUSES = new Set([502, 503, 504]);

function unformattedError(status: number): ApiError {
  return GATEWAY_STATUSES.has(status)
    ? new ApiError(status, UNREACHABLE, 'Hilo no responde ahora mismo', null)
    : new ApiError(status, `HTTP_${status}`, 'Hilo respondió con un error inesperado', null);
}

/** Hilo no está disponible (sin red, reiniciándose o con la base caída): basta con reintentar más tarde. */
export function isUnavailable(error: unknown): boolean {
  return error instanceof NetworkError
    || (error instanceof ApiError && (error.code === UNREACHABLE || error.code === 'SERVICE_UNAVAILABLE'));
}

let csrfToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

/** Se invoca ante cualquier 401: la sesión ya no es válida. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

export type Query = Record<string, string | number | undefined | null>;

export interface RequestOptions {
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
}

export function buildUrl(path: string, query?: Query): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const search = params.toString();
  return `${API_PREFIX}${path}${search ? `?${search}` : ''}`;
}

function isErrorBody(value: unknown): value is { error: ApiErrorBody } {
  if (!value || typeof value !== 'object' || !('error' in value)) return false;
  const error = value.error;
  return !!error && typeof error === 'object' && 'code' in error && typeof error.code === 'string';
}

/**
 * Sube un archivo como cuerpo binario (POST /conversations/:id/attachments). XMLHttpRequest para poder
 * informar del progreso, que fetch no ofrece al subir. Mismos errores que api().
 */
export function uploadFile<T>(path: string, file: File, onProgress: (fraction: number) => void): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', buildUrl(path));
    xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', 'application/octet-stream');
    xhr.setRequestHeader('x-file-name', encodeURIComponent(file.name));
    xhr.setRequestHeader('accept', 'application/json');
    if (csrfToken) xhr.setRequestHeader('x-csrf-token', csrfToken);
    xhr.upload.onprogress = (event) => { if (event.lengthComputable) onProgress(event.loaded / event.total); };
    xhr.onerror = () => reject(new NetworkError());
    xhr.onabort = () => reject(new NetworkError());
    xhr.onload = () => {
      let body: unknown;
      try {
        body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        body = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) return resolve(body as T);
      if (xhr.status === 401) unauthorizedHandler?.();
      if (isErrorBody(body)) return reject(new ApiError(xhr.status, body.error.code, body.error.message, body.error.requestId ?? null));
      reject(unformattedError(xhr.status));
    };
    xhr.send(file);
  });
}

export async function api<T>(method: string, path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (MUTATING.has(method) && csrfToken) headers['x-csrf-token'] = csrfToken;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      credentials: 'same-origin',
      body: options.body === undefined ? null : JSON.stringify(options.body),
      signal: options.signal ?? null,
    });
  } catch (error) {
    if (options.signal?.aborted) throw error;
    throw new NetworkError();
  }

  const text = await response.text();
  let body: unknown = null;
  if (text && (response.headers.get('content-type') ?? '').includes('application/json')) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    if (response.status === 401) unauthorizedHandler?.();
    if (isErrorBody(body)) {
      throw new ApiError(response.status, body.error.code, body.error.message, body.error.requestId ?? null);
    }
    // Un proxy o un servidor caído pueden responder sin el formato de la API.
    throw unformattedError(response.status);
  }
  return body as T;
}
